import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Shield, Users, TrendingUp, Award, Zap, Store, Package, Heart, Building2, CheckCircle, Phone, Sparkles } from 'lucide-react';
import axios from 'axios';
import { Partner, Plan } from '../types';
import { formatCurrency } from '../utils/formatters';

const API_URL = process.env.REACT_APP_API_URL || 'http://fabio-ubuntu:8082/api';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    loadPartners();
    loadPlans();
  }, []);

  const loadPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/partners/active`);
      setPartners(response.data);
    } catch (error) {
      console.error('Erro ao carregar parceiros:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/plans/active`);
      setPlans(response.data);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 via-green-500 to-white">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-lg rounded-3xl mb-6 shadow-2xl border border-white/30">
            <CreditCard className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-6xl font-extrabold text-white mb-4 tracking-tight drop-shadow-lg">
            Amigos do HMG
          </h1>
          <p className="text-2xl text-white font-light drop-shadow-md">
            Seu cartão de benefícios inteligente
          </p>
        </div>

        {/* Benefits Grid */}
        {/* <div className="grid md:grid-cols-3 gap-6 mb-16 max-w-5xl mx-auto">
          <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 border border-white/30 hover:bg-white/30 transition-all duration-300 shadow-lg">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-md">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2 drop-shadow">Economia Real</h3>
            <p className="text-white drop-shadow-sm">
              Descontos exclusivos em diversos parceiros
            </p>
          </div>

          <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 border border-white/30 hover:bg-white/30 transition-all duration-300 shadow-lg">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-md">
              <Award className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2 drop-shadow">Vantagens Únicas</h3>
            <p className="text-white drop-shadow-sm">
              Benefícios pensados especialmente pra você
            </p>
          </div>

          <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 border border-white/30 hover:bg-white/30 transition-all duration-300 shadow-lg">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-md">
              <Zap className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2 drop-shadow">Fácil e Rápido</h3>
            <p className="text-white drop-shadow-sm">
              Use seus benefícios em segundos
            </p>
          </div>
        </div> */}

        {/* About Program Section - Amigos do Hospital */}
        <div className="mb-16 max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-green-600 to-green-500 p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-lg rounded-full mb-4 shadow-lg border-2 border-white/30">
                <Heart className="w-10 h-10 text-white" />
              </div>
              {/* <h2 className="text-4xl font-extrabold text-white mb-4 drop-shadow-lg">
                O que é o Amigos do Hospital?
              </h2> */}
              <h2 className="text-xl text-white/95 max-w-6xl mx-auto leading-relaxed drop-shadow">
                O Amigos do Hospital é um programa de apoio solidário criado para fortalecer o trabalho do Hospital Manoel Gonçalves.
                Por meio dele, qualquer pessoa pode contribuir mensalmente com uma doação e, em troca, receber benefícios exclusivos.
              </h2>
            </div>

            {/* Content */}
            <div className="p-8 md:p-12">
              {/* Program Description */}
              <div className="mb-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Um cartão que transforma vidas
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      O Amigos do Hospital é um cartão de contribuição voluntária que ajuda a manter e ampliar os serviços do hospital,
                      garantindo mais qualidade, acolhimento e recursos para quem mais precisa.
                    </p>
                  </div>
                </div>
              </div>

              {/* Benefits Section */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Benefícios para quem participa
                  </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl hover:bg-green-100 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-800 font-medium">
                      Descontos em exames laboratoriais
                    </p>
                  </div>

                  <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl hover:bg-green-100 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-800 font-medium">
                      Descontos em exames de imagem
                    </p>
                  </div>

                  <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl hover:bg-green-100 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-800 font-medium">
                      Descontos em consultas eletivas
                    </p>
                  </div>

                  <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl hover:bg-green-100 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-800 font-medium">
                      Vantagens em comércios parceiros da cidade
                    </p>
                  </div>

                  <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl hover:bg-green-100 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-800 font-medium">
                      Participação em um movimento que fortalece a saúde da comunidade
                    </p>
                  </div>

                  <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl hover:bg-green-100 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-800 font-medium">
                      Sentimento de apoio e solidariedade, transformando vidas
                    </p>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-2xl p-8 text-center shadow-xl">
                <h3 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">
                  Torne-se hoje mesmo um doador!
                </h3>
                <p className="text-lg text-white/95 mb-6 drop-shadow">
                  Entre em contato com o Setor Comercial do Hospital
                </p>

                <a
                  href="tel:+5537999933406"
                  className="inline-flex items-center gap-3 bg-white text-green-700 font-bold text-xl px-8 py-4 rounded-xl hover:bg-green-50 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <Phone className="w-6 h-6" />
                  <span>(37) 9 9993-3406</span>
                </a>

                <div className="mt-8 pt-6 border-t border-white/30">
                  <p className="text-2xl font-bold text-white drop-shadow-lg italic">
                    Quem doa, cuida. Quem cuida, transforma.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Planos Section */}
        <div className="mb-16 max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl mb-4 shadow-lg border border-white/30">
              <Package className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white drop-shadow-lg">
              Nossos Planos
            </h2>
            <p className="text-xl text-white mt-2 drop-shadow">
              Escolha o plano ideal para você
            </p>
          </div>

          {loadingPlans ? (
            <div className="text-center text-white">
              <div className="animate-pulse">Carregando planos...</div>
            </div>
          ) : plans.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 w-full md:w-80"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.nome}
                  </h3>
                  {plan.descricao && (
                    <p className="text-gray-600 mb-4 text-sm">
                      {plan.descricao}
                    </p>
                  )}
                  <div className="flex items-baseline mb-4">
                    <span className="text-4xl font-extrabold text-green-600">
                      {formatCurrency(plan.valor)}
                    </span>
                    <span className="text-gray-500 ml-2">/mês</span>
                  </div>
                  <button
                    onClick={() => navigate('/cadastro')}
                    className="w-full bg-gradient-to-r from-green-500 to-green-700 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-green-800 transition-all shadow-md"
                  >
                    Contratar Plano
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-white bg-white/20 backdrop-blur-lg rounded-2xl p-8 border border-white/30">
              <p>Nenhum plano disponível no momento.</p>
            </div>
          )}
        </div>

        {/* Parceiros Section */}
        <div className="mb-16 max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl mb-4 shadow-lg border border-white/30">
              <Store className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white drop-shadow-lg">
              Parceiros Conveniados
            </h2>
            <p className="text-xl text-white mt-2 drop-shadow">
              Descontos exclusivos em diversos estabelecimentos
            </p>
          </div>

          {loadingPartners ? (
            <div className="text-center text-white">
              <div className="animate-pulse">Carregando parceiros...</div>
            </div>
          ) : partners.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-6">
              {partners.map((partner) => (
                <div
                  key={partner.id}
                  className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 w-full md:w-80"
                >
                  {partner.logo && (
                    <div className="mb-4 h-32 flex items-center justify-center bg-gray-50 rounded-xl overflow-hidden">
                      <img
                        src={`${API_URL.replace('/api', '')}/storage/${partner.logo}`}
                        alt={partner.nome_fantasia || partner.name}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {partner.nome_fantasia || partner.name}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    {partner.endereco || partner.address}, {partner.numero} - {partner.bairro}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-green-600">
                      {partner.desconto_percentual || partner.discount_percentage}% OFF
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-white bg-white/20 backdrop-blur-lg rounded-2xl p-8 border border-white/30">
              <p>Nenhum parceiro disponível no momento.</p>
            </div>
          )}
        </div>

        {/* Login Cards */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-8">
            Escolha como deseja acessar
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Associate Login Card */}
            <button
              onClick={() => navigate('/associado/login')}
              className="group relative bg-white rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400 to-green-600 rounded-bl-full opacity-10 group-hover:opacity-20 transition-opacity"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Users className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-3xl font-bold text-gray-900 mb-3">
                  Sou Associado
                </h3>

                <p className="text-gray-600 mb-6">
                  Acesse seus benefícios, veja parceiros disponíveis e economize no dia a dia
                </p>

                <div className="flex items-center text-green-600 font-semibold group-hover:gap-3 transition-all">
                  <span>Acessar minha conta</span>
                  <svg
                    className="w-5 h-5 group-hover:translate-x-2 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>

            {/* Admin Login Card */}
            <button
              onClick={() => navigate('/admin/login')}
              className="group relative bg-white rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400 to-green-700 rounded-bl-full opacity-10 group-hover:opacity-20 transition-opacity"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-800 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-3xl font-bold text-gray-900 mb-3">
                  Sou Administrador
                </h3>

                <p className="text-gray-600 mb-6">
                  Gerencie associados, parceiros e acompanhe todo o sistema
                </p>

                <div className="flex items-center text-green-700 font-semibold group-hover:gap-3 transition-all">
                  <span>Acessar painel admin</span>
                  <svg
                    className="w-5 h-5 group-hover:translate-x-2 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-8 border border-white/30 shadow-xl">
            <h3 className="text-2xl font-bold text-white mb-4 drop-shadow">
              Ainda não é associado?
            </h3>
            <p className="text-white mb-6 drop-shadow-sm">
              Cadastre-se agora e aproveite descontos exclusivos em diversos parceiros
            </p>
            <button
              onClick={() => navigate('/cadastro')}
              className="inline-flex items-center px-8 py-4 bg-white text-green-700 font-semibold rounded-xl hover:bg-green-50 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Quero me Cadastrar
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-white text-sm drop-shadow">
            Cartão HMG 2025 - Todos os direitos reservados
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
